import { DEALER_SEARCH_KEY } from '@/app/components/shared/dealer/utils/dealer-constant';
import type {
  ContactInfo,
  Dealer,
  DealerGrouping,
} from '@/services/contentstack/models/dealer';

export const isDealerCV = process.env.NEXT_PUBLIC_WEB === 'cv';

type DealerServiceType = 'sale' | 'aftersale' | 'bp';
type DealerCampaignType = 'campaign';

const getDealerStatus = (
  dealer: Dealer,
  key: DealerServiceType | DealerCampaignType
): boolean => {
  if (!dealer) return false;

  const suffix = isDealerCV ? 'cv' : 'lcv';

  if (key === 'campaign') return !!dealer.sale?.[`campaign_${suffix}`];

  return !!dealer[key]?.[`active_${suffix}`];
};

export const isDealerActiveSale = (dealer: Dealer) =>
  getDealerStatus(dealer, 'sale');

export const isDealerActiveAfterSale = (dealer: Dealer) =>
  getDealerStatus(dealer, 'aftersale');

export const isDealerActiveBodyPaint = (dealer: Dealer) =>
  getDealerStatus(dealer, 'bp');

export const isDealerRegisterable = (dealer: Dealer) =>
  getDealerStatus(dealer, 'campaign');

export const getDealerFacebook = (dealer: ContactInfo) => {
  const facebookLink = `https://www.facebook.com/`;
  return {
    cv: {
      name: dealer?.fb_cv_name || dealer?.fb_cv || '',
      link: `${facebookLink}${dealer?.fb_cv}`,
    },
    lcv: {
      name: dealer?.fb_lcv_name || dealer?.fb_lcv || '',
      link: `${facebookLink}${dealer?.fb_lcv}`,
    },
  };
};

export const getDealerMessenger = (dealer: ContactInfo) => {
  const messengerLink = `https://m.me/`;
  return {
    cv: {
      name: dealer?.messenger_cv_name || dealer?.messenger_cv || '',
      link: `${messengerLink}${dealer?.messenger_cv}`,
    },
    lcv: {
      name: dealer?.messenger_lcv_name || dealer?.messenger_lcv || '',
      link: `${messengerLink}${dealer?.messenger_lcv}`,
    },
  };
};

export const getDealerLine = (dealer: ContactInfo) => {
  const lineLink = `https://line.me/ti/p/`;
  return {
    cv: {
      name: dealer?.line_cv ?? '',
      link: `${lineLink}${dealer?.line_cv}`,
    },
    lcv: {
      name: dealer?.line_lcv ?? '',
      link: `${lineLink}${dealer?.line_lcv}`,
    },
  };
};

export const getDealerGoogleMapUrl = (dealer: Dealer, name?: string) => {
  return (
    dealer?.google_map_url ||
    `https://www.google.com/maps/search/${name}/${dealer?.lat},${dealer?.lng}`
  );
};

export const groupingDealersForSearch = (dealers: DealerGrouping[]) => {
  if (dealers.length === 0) return [];

  const result: DealerGrouping[] = [];

  for (const dealer of dealers) {
    if (isDealerActiveSale(dealer)) {
      result.push({
        ...dealer,
        groupForSearch: {
          id: `${dealer.uid}-${DEALER_SEARCH_KEY.Showroom}`,
          type: DEALER_SEARCH_KEY.Showroom,
          name: dealer?.sale?.name_th || '',
          address: dealer?.sale?.address || '',
        },
      });
    }

    if (isDealerActiveAfterSale(dealer)) {
      result.push({
        ...dealer,
        groupForSearch: {
          id: `${dealer.uid}-${DEALER_SEARCH_KEY.Service}`,
          type: DEALER_SEARCH_KEY.Service,
          name: dealer?.aftersale?.name_th || '',
          address: dealer?.aftersale?.address || '',
        },
      });
    }

    if (isDealerActiveBodyPaint(dealer)) {
      result.push({
        ...dealer,
        groupForSearch: {
          id: `${dealer.uid}-${DEALER_SEARCH_KEY.BodyPaint}`,
          type: DEALER_SEARCH_KEY.BodyPaint,
          name: dealer?.bp?.name_th || '',
          address: dealer?.bp?.address || '',
        },
      });
    }
  }

  return result;
};
